import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import intersectionWith from 'lodash/intersectionWith';
import { createSelector } from 'reselect';

import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import { entities } from 'pages/store/schema';
import { getValue, getValues } from 'pages/store/selectors';

export const GatewayType = {
  BARCLAYCARD: 'barclaycard',
  BNP_PARIBAS: 'bnp-paribas',
  CAYAN: 'cayan',
  CONCARDIS: 'concardis',
  ELEMENT_EXPRESS: 'element-express',
  FIRST_DATA: 'first-data',
  IZETTLE: 'izettle',
  LIGHTSPEED_PAYMENTS: 'lightspeed-payments',
  MERCURY_CANADA: 'mercury-canada',
  MERCURY_EMV: 'mercury-emv',
  MERCURY_PAYMENT_SYSTEMS: 'mercury-payment-systems',
  TYRO: 'tyro',
  VANTIV_CANADA: 'vantiv-canada',
  VANTIV_TRIPOS: 'vantiv-tripos',
  VANTIV_US: 'vantiv-us',
  WORLDLINE: 'worldline',
};

export const DEFAULT_CARD_MAPPINGS = [
  {
    key: PaymentTransaction.SupportedCardType.VISA,
    label: 'Visa',
  },
  {
    key: PaymentTransaction.SupportedCardType.MASTERCARD,
    label: 'MasterCard',
  },
  {
    key: PaymentTransaction.SupportedCardType.DISCOVER,
    label: 'Discover',
  },
  {
    key: PaymentTransaction.SupportedCardType.AMERICAN_EXPRESS,
    label: 'American Express',
  },
  {
    key: PaymentTransaction.SupportedCardType.DEBIT,
    label: 'Debit',
  },
  {
    key: PaymentTransaction.SupportedCardType.INTERAC,
    label: 'Interac',
  },
  {
    key: PaymentTransaction.SupportedCardType.BANCONTACT,
    label: 'Bancontact',
  },
  {
    key: PaymentTransaction.SupportedCardType.IDEAL,
    label: 'iDEAL',
  },
];

export const filterDefaultCardMappings = (availableCardMappings) =>
  intersectionWith(
    DEFAULT_CARD_MAPPINGS,
    availableCardMappings,
    (reference, current) => reference.key === current.cardSchemeID
  );

export const findCardMappingWithKey = (cardMappings, key) => {
  const cardMapping = find(cardMappings, { cardSchemeID: key });
  return cardMapping ? { ...cardMapping, ...find(DEFAULT_CARD_MAPPINGS, { key }, {}) } : null;
};

export const getLabelledGatewayCardMappings = (gatewayConfig) => {
  const cardMappingsFromGateway = get(gatewayConfig, 'cardSchemeMappings.CardSchemeMapping', []);
  const filteredDefaultCardMappings = filterDefaultCardMappings(cardMappingsFromGateway);
  return filteredDefaultCardMappings.map(({ key }) =>
    findCardMappingWithKey(cardMappingsFromGateway, key)
  );
};

export const getLightspeedPaymentGateways = createSelector(
  getValues(entities.gatewayConfig),
  (gateways) => {
    const accountID = window.merchantos.account.id;
    return filter(gateways, (gatewayConfig) => {
      const authorizedShops = JSON.parse(get(gatewayConfig, 'parameters.authorizedShops', '[]'));
      if (!Array.isArray(authorizedShops)) {
        return (
          get(gatewayConfig, 'gatewayID') === GatewayType.LIGHTSPEED_PAYMENTS &&
          get(authorizedShops, `retail[${accountID}]`, []).length > 0
        );
      }
      return null;
    });
  }
);

export const getLightspeedPaymentCardMappings = createSelector(
  getLightspeedPaymentGateways,
  (gateways) =>
    // all lightspeed payment gateways utilize the same mappings
    gateways.length ? getLabelledGatewayCardMappings(gateways[0]) : []
);

export const getLightspeedPaymentParameters = createSelector(
  getLightspeedPaymentGateways,
  (gateways) =>
    // all lightspeed payment gateways utilize the same parameters
    gateways.length ? get(gateways[0], 'parameters') : {}
);

export const getIntegratedPaymentGateways = createSelector(
  getValues(entities.gatewayConfig),
  (gateways) =>
    filter(
      gateways,
      (gatewayConfig) => get(gatewayConfig, 'gatewayID') !== GatewayType.LIGHTSPEED_PAYMENTS
    )
);

export const getGatewayCardMappings = (gatewayConfigId) =>
  createSelector(getValue(entities.gatewayConfig, gatewayConfigId), (gatewayConfig) =>
    getLabelledGatewayCardMappings(gatewayConfig)
  );
